import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Mail, MapPin, Loader2 } from "lucide-react";
import { CALENDLY_URL, EMAIL, LOCATION } from "../lib/site";
import Seo from "../components/Seo";
import { businessSchema } from "../lib/seoSchemas";
import { useContent } from "../lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const { t } = useContent();
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Load Calendly widget script
    const id = "calendly-widget-script";
    if (!document.getElementById(id)) {
      const script = document.createElement("script");
      script.id = id;
      script.src = "https://assets.calendly.com/assets/external/widget.js";
      script.async = true;
      document.body.appendChild(script);
    }
  }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      toast.success(res.data?.message || "Thanks. We will be in touch.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : Array.isArray(detail) && detail[0]?.msg
          ? detail[0].msg
          : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      <Seo
        title={t("contact.seo.title")}
        description={t("contact.seo.description")}
        schemas={[businessSchema]}
      />
      {/* HERO */}
      <section className="bg-white py-20 lg:py-28 border-b border-ink-line" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-6">{t("contact.hero.eyebrow")}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-brand-700 leading-[1.02] tracking-tight max-w-4xl">
            {t("contact.hero.heading")}
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">
            {t("contact.hero.intro")}
          </p>
        </div>
      </section>

      <section className="bg-white py-20" data-testid="contact-grid">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* CONTACT INFO */}
            <aside className="lg:col-span-4">
              <div className="eyebrow mb-4">Direct</div>
              <h2 className="font-serif text-3xl text-brand-700 leading-tight mb-8">
                {t("contact.direct.heading")}
              </h2>
              <ul className="space-y-6">
                <li className="flex items-start gap-4" data-testid="contact-info-email">
                  <Mail className="w-5 h-5 text-brand-700 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-1">
                      Email
                    </div>
                    <a
                      href={`mailto:${EMAIL}`}
                      className="text-ink-soft hover:text-brand-700 break-all"
                    >
                      {EMAIL}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-location">
                  <MapPin className="w-5 h-5 text-brand-700 mt-1 shrink-0" />
                  <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-brand-500 mb-1">
                      Location
                    </div>
                    <div className="text-ink-soft">{LOCATION}</div>
                    <div className="text-ink-muted text-sm">{t("contact.direct.serving_line")}</div>
                  </div>
                </li>
              </ul>

              <div className="mt-10">
                <a
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 text-white px-7 py-4 text-sm font-medium transition-colors"
                  data-testid="contact-book-call-btn"
                >
                  Book a Call <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </aside>

            {/* FORM */}
            <form
              className="lg:col-span-8 lg:pl-8 lg:border-l lg:border-ink-line"
              onSubmit={handleSubmit}
              data-testid="contact-form"
            >
              <div className="eyebrow mb-4">{t("contact.form.eyebrow")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="contact-input-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors"
                    data-testid="contact-input-company"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs uppercase tracking-[0.2em] text-brand-500 mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="w-full border border-ink-line bg-white px-4 py-3 text-ink focus:outline-none focus:border-brand-700 transition-colors resize-y"
                    data-testid="contact-input-message"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="mt-8 inline-flex items-center gap-2 bg-brand-700 hover:bg-brand-800 disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 text-sm font-medium transition-colors"
                data-testid="contact-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    {t("contact.form.submit")} <ArrowUpRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* CALENDLY EMBED */}
      <section className="bg-brand-100 py-20" data-testid="contact-calendly">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="eyebrow mb-4">Schedule</div>
          <h2 className="font-serif text-4xl text-brand-700 leading-tight mb-8 max-w-2xl">
            {t("contact.schedule.heading")}
          </h2>
          <div
            className="calendly-inline-widget bg-white border border-brand-200"
            data-url={`${CALENDLY_URL}?hide_event_type_details=0&hide_gdpr_banner=1&primary_color=3B1E5A`}
            style={{ minWidth: "320px", height: "720px" }}
            data-testid="calendly-embed"
          />
        </div>
      </section>
    </div>
  );
}
